import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  Flame,
  Edit3,
  Trash2,
  Calendar,
  Share2,
} from "lucide-react";
import { ShareDialog } from "@/components/share/share-dialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function HabitCard({ habit, onCheckIn, onEdit, onDelete }) {
  const {
    id,
    name,
    description,
    cadence,
    targetPerWeek,
    streak = 0,
    thisWeekCount = 0,
    completionRate = 0,
  } = habit;

  const isCheckedInToday = thisWeekCount > 0; // Simplified check
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cadenceLabelKey =
    cadence === "weekdays"
      ? "habits.create.cadenceWeekdays"
      : cadence === "weekends"
      ? "habits.create.cadenceWeekends"
      : cadence === "weekly"
      ? "habits.create.cadenceWeekly"
      : cadence === "custom"
      ? "habits.create.cadenceCustom"
      : "habits.card.dailyCadence";

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/habits/${id}`)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{name}</CardTitle>
            {description && (
              <CardDescription className="mt-1 line-clamp-2">
                {description}
              </CardDescription>
            )}
          </div>
          {streak > 0 && (
            <div className="flex items-center gap-1 text-orange-500">
              <Flame className="h-4 w-4" />
              <span className="text-sm font-semibold">{streak}</span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Progress */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">
              {t("habits.card.thisWeek")}
            </span>
            <span className="font-medium">
              {thisWeekCount} / {targetPerWeek}
            </span>
          </div>
          <Progress value={completionRate} className="h-2" />
        </div>

        {/* Cadence Badge */}
        <div className="flex items-center gap-2">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="text-xs">
            {t(cadenceLabelKey)}
          </Badge>
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-4 border-t">
        <Button
          size="sm"
          className="flex-1"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            onCheckIn();
          }}
          disabled={isCheckedInToday}
          variant={isCheckedInToday ? "outline" : "default"}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          {isCheckedInToday
            ? t("habits.card.checkedIn")
            : t("habits.card.checkIn")}
        </Button>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Edit3 className="h-4 w-4" />
        </Button>
        <ShareDialog 
          itemId={id} 
          type="habit"
          itemTitle={name}
          trigger={
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => e.stopPropagation()}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          }
        />
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </CardFooter>
    </Card>
  );
}
